import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function TimezoneMaster({ token }) {
  const emptyForm = {
    country: '',
    tz_name: '',
    display_name: '',
    utc_offset_minutes: '',
    is_active: true
  };

  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filterCountry, setFilterCountry] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const auth = { Authorization: `Bearer ${token}` };

  const loadCountries = async () => {
    try {
      const res = await fetch('/api/v1/core/countries', { headers: auth });
      const data = await res.json();
      setCountries(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };

  const loadRows = async () => {
    try {
      const url = filterCountry
        ? `/api/v1/core/timezones?country=${encodeURIComponent(filterCountry)}&all=1`
        : '/api/v1/core/timezones?all=1';
      const res = await fetch(url, { headers: auth });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load timezones');
        return;
      }
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading timezones');
    }
  };

  useEffect(() => { loadCountries(); }, [token]);
  useEffect(() => { loadRows(); }, [token, filterCountry]);

  const handleSave = async () => {
    const country = String(form.country || '').trim();
    const tzName = String(form.tz_name || '').trim();
    if (!country) { setError('Country is required'); return; }
    if (!tzName) { setError('Timezone (IANA) is required'); return; }
    const payload = {
      country,
      tz_name: tzName,
      display_name: String(form.display_name || '').trim(),
      utc_offset_minutes: form.utc_offset_minutes === '' || form.utc_offset_minutes == null ? null : Number(form.utc_offset_minutes),
      is_active: form.is_active !== false
    };
    try {
      const res = await fetch(editingId ? `/api/v1/core/timezones/${editingId}` : '/api/v1/core/timezones', {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      setError('');
      setMessage(editingId ? 'Timezone updated' : 'Timezone added');
      setForm(emptyForm);
      setEditingId(null);
      loadRows();
    } catch {
      setError('Network error while saving timezone');
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      country: row.country || '',
      tz_name: row.tz_name || '',
      display_name: row.display_name || '',
      utc_offset_minutes: row.utc_offset_minutes != null ? String(row.utc_offset_minutes) : '',
      is_active: Boolean(row.is_active)
    });
    setError(''); setMessage('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this timezone? Properties already using it will keep their value but it will no longer appear in the dropdown.')) return;
    try {
      const res = await fetch(`/api/v1/core/timezones/${id}`, { method: 'DELETE', headers: auth });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to deactivate'); return; }
      setError(''); setMessage('Timezone deactivated');
      loadRows();
    } catch {
      setError('Network error while deactivating');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(''); setMessage('');
  };

  const fmtOffset = (mins) => {
    if (mins == null) return '-';
    const sign = mins >= 0 ? '+' : '−';
    const abs = Math.abs(Number(mins));
    const h = Math.floor(abs / 60).toString().padStart(2, '0');
    const m = (abs % 60).toString().padStart(2, '0');
    return `UTC${sign}${h}:${m}`;
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Timezone Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#166534', padding: '0.6rem 0.9rem', borderRadius: '6px', marginBottom: '0.8rem' }}>{message}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Timezone' : 'Add Timezone'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.9rem' }}>
          <label style={{ display: 'grid', gap: '0.3rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Country *</span>
            <select
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
            >
              <option value="">-- Select Country --</option>
              {countries.map((c) => (
                <option key={c.id} value={c.country_name}>{c.country_name}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.3rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>IANA Name *</span>
            <input
              placeholder="e.g. Asia/Kolkata"
              value={form.tz_name}
              onChange={(e) => setForm({ ...form, tz_name: e.target.value })}
              style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
            />
          </label>
          <label style={{ display: 'grid', gap: '0.3rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Display Name</span>
            <input
              placeholder="e.g. India Standard Time (IST, UTC+05:30)"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
            />
          </label>
          <label style={{ display: 'grid', gap: '0.3rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>UTC Offset (minutes)</span>
            <input
              type="number"
              placeholder="e.g. 330 for IST"
              value={form.utc_offset_minutes}
              onChange={(e) => setForm({ ...form, utc_offset_minutes: e.target.value })}
              style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop: '0.9rem', display: 'flex', gap: '0.7rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding: '0.6rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            {editingId ? 'Update Timezone' : 'Add Timezone'}
          </button>
          {editingId && (
            <button onClick={cancelEdit} className="btn-muted" style={{ padding: '0.6rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '1rem 0 0.5rem' }}>
        <h3 style={{ margin: 0 }}>Timezones</h3>
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#475569' }}>Filter by country:</span>
        <select
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
          style={{ padding: '0.45rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
        >
          <option value="">All countries</option>
          {countries.map((c) => (
            <option key={c.id} value={c.country_name}>{c.country_name}</option>
          ))}
        </select>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: '#7f8c8d' }}>No timezones found{filterCountry ? ` for ${filterCountry}` : ''}.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.7rem', textAlign: 'left' }}>Country</th>
                <th style={{ padding: '0.7rem', textAlign: 'left' }}>IANA Name</th>
                <th style={{ padding: '0.7rem', textAlign: 'left' }}>Display Name</th>
                <th style={{ padding: '0.7rem', textAlign: 'left' }}>UTC Offset</th>
                <th style={{ padding: '0.7rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '0.7rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.country}</td>
                  <td style={{ padding: '0.6rem', fontFamily: 'monospace' }}>{r.tz_name}</td>
                  <td style={{ padding: '0.6rem' }}>{r.display_name || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{fmtOffset(r.utc_offset_minutes)}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_active ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.4rem' }}>
                    <button onClick={() => handleEdit(r)} style={{ border: 'none', borderRadius: '4px', padding: '0.4rem 0.65rem', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                    {r.is_active && (
                      <button onClick={() => handleDelete(r.id)} className="btn-danger" style={{ border: 'none', borderRadius: '4px', padding: '0.4rem 0.65rem', cursor: 'pointer' }}>Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TimezoneMaster;
